/**
 * Returns the appropriate title key for BulkActionErrorsDrawer based on errorType.
 * Centralizes the title logic so it doesn't need to be repeated in each page.
 *
 * @param {Object} errorData - The errorData object with errorType property
 * @returns {string} - The i18n key for the drawer title
 */
export function getErrorDrawerTitle(errorData) {
  if (errorData?.errorType === 'import') {
    return 'global.import.errors.title';
  }
  return 'global.bulk.delete.errors.title';
}

/**
 * Builds the error payload for BulkActionErrorsDrawer when import fails.
 * Centralizes the transformation from API import error response to the flat
 * structure expected by the drawer, so it can be reused across areas, users, etc.
 *
 * @param {Object} res - API response with errorMessage and errors[] (rows with errors[].columnName, errorMessage)
 * @param {Function} getIntl - getIntl() from umi (for formatMessage)
 * @returns {{ errorMessage: string, originalErrorMessage: string, errors: Array, errorType: 'import' } | null}
 *   Returns null if res has no errorMessage (no error to show).
 */
export function getBulkImportErrorData(res, getIntl) {
  if (!res?.errorMessage) return null;

  const intl = getIntl();
  const importErrors = [];

  if (res.errors && res.errors.length > 0) {
    res.errors.forEach((errorRow) => {
      if (errorRow.errors && errorRow.errors.length > 0) {
        errorRow.errors.forEach((error) => {
          importErrors.push({
            id: `${errorRow.rowNumber}-${error.columnName || 'unknown'}`,
            rowNumber: errorRow.rowNumber,
            columnName: error.columnName || '',
            errorMessage: error.errorMessage,
            ...(error.errorCode != null && { errorCode: error.errorCode }),
            ...(error.metadata != null && { metadata: error.metadata }),
          });
        });
      }
    });
  }

  const errorCount = importErrors.length;
  const rowsWithErrors = res.errors ? res.errors.length : 0;
  const errorMessage = intl.formatMessage(
    { id: 'global.error.import.detailed' },
    { errorCount, rowsWithErrors, originalMessage: res.errorMessage || '' },
  );

  return {
    errorMessage,
    originalErrorMessage: res.errorMessage,
    errors: importErrors,
    errorType: 'import',
  };
}
